import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { DirectionalChevron } from "@/components/core/DirectionalChevron";
import { Link } from "@/i18n/navigation";
import { $api } from "@/lib/api-client";

interface UseAddToCartOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /** Show toast notification when item is added (use only for first add, not quantity changes) */
  showAddedToast?: boolean;
  /** Whether the item is a service (affects toast link destination) */
  isService?: boolean;
}

// Unique toast ID to prevent duplicate toasts
const ADD_TO_CART_TOAST_ID = "add-to-cart-toast";
const VENDOR_NOT_VERIFIED_TOAST_ID = "vendor-not-verified-toast";

/** Check vendor verification and show toast if not verified. Returns true if blocked.
 *  Only blocks when is_verified is explicitly false. When null/undefined (e.g., vendor
 *  data not loaded or not included in list responses), we let it through and rely on
 *  the backend to reject if needed.
 *  `message` is the already-translated text — this is a plain function (not a hook),
 *  so callers pass `t("cart.toasts.vendorNotVerified")`. */
export const checkVendorNotVerified = (
  isVerified: boolean | null | undefined,
  message: string,
): boolean => {
  if (isVerified !== false) return false;
  toast.error(message, {
    id: VENDOR_NOT_VERIFIED_TOAST_ID,
  });
  return true;
};

/**
 * Hook for adding items to cart
 * @example
 * // For first add (shows toast)
 * const addToCart = useAddToCart({
 *   showAddedToast: true,
 *   onSuccess: () => { quantity.refetch(); },
 * });
 *
 * // For quantity increment (no toast)
 * const addToCart = useAddToCart({
 *   onSuccess: () => { quantity.refetch(); },
 * });
 */
export const useAddToCart = (options?: UseAddToCartOptions) => {
  const t = useTranslations("cart");
  // Determine the correct cart tab based on item type
  const cartUrl = options?.isService
    ? "/shopping-cart?tab=services"
    : "/shopping-cart?tab=products";

  return $api.useMutation("post", "/cart/add", {
    onSuccess: () => {
      if (options?.showAddedToast) {
        toast.success(t("toasts.added"), {
          id: ADD_TO_CART_TOAST_ID,
          action: (
            <Link
              href={cartUrl}
              className="text-text-secondary flex items-end mr-auto gap-1"
            >
              <span className="">{t("toasts.goToCart")}</span>
              <DirectionalChevron size={12} className="mb-1" />
            </Link>
          ),
        });
      }
      options?.onSuccess?.();
    },
    onError: (...args) => {
      // openapi-react-query throws the raw response body (plain object), not an Error.
      // Check for vendor-not-verified error code in the response.
      const error: unknown = args[0];
      const serialized = JSON.stringify(error);
      if (serialized.includes("VENDOR_NOT_VERIFIED")) {
        toast.error(t("toasts.vendorNotVerified"), {
          id: VENDOR_NOT_VERIFIED_TOAST_ID,
        });
      }
    },
  });
};
